import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  increment
} from 'firebase/firestore';
import emailService from './emailService';

/**
 * Instructor Service
 * Manages course creators and their profiles
 */

const COLLECTIONS = {
  INSTRUCTORS: 'instructors',
  APPLICATIONS: 'instructorApplications',
  COURSES: 'courses',
  SESSIONS: 'sessions',
  ENROLLMENTS: 'enrollments',
  REVIEWS: 'courseReviews',
  EARNINGS: 'instructorEarnings'
};

class InstructorService {
  /**
   * Apply to become an instructor
   */
  async applyAsInstructor(applicationData) {
    try {
      const applicationsRef = collection(db, COLLECTIONS.APPLICATIONS);
      
      const application = {
        ...applicationData,
        status: 'pending', // pending, approved, rejected
        submittedAt: Timestamp.now(),
        reviewedAt: null,
        reviewedBy: null,
        reviewNotes: ''
      };
      
      const docRef = await addDoc(applicationsRef, application);
      return { id: docRef.id, ...application };
    } catch (error) {
      console.error('Error submitting application:', error);
      throw error;
    }
  }

  /**
   * Get instructor application status
   */
  async getApplicationStatus(userId) {
    try {
      const applicationsRef = collection(db, COLLECTIONS.APPLICATIONS);
      const q = query(
        applicationsRef,
        where('userId', '==', userId),
        orderBy('submittedAt', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    } catch (error) {
      console.error('Error getting application:', error);
      return null;
    }
  }

  /**
   * Approve instructor application
   */
  async approveApplication(applicationId, adminId, notes = '') {
    try {
      const appRef = doc(db, COLLECTIONS.APPLICATIONS, applicationId);
      const appDoc = await getDoc(appRef);
      
      if (!appDoc.exists()) {
        throw new Error('Application not found');
      }
      
      const appData = appDoc.data();
      
      // Update application status
      await updateDoc(appRef, {
        status: 'approved',
        reviewedAt: Timestamp.now(),
        reviewedBy: adminId,
        reviewNotes: notes
      });
      
      // Create instructor profile
      const instructorsRef = collection(db, COLLECTIONS.INSTRUCTORS);
      const instructor = {
        userId: appData.userId,
        name: appData.name,
        email: appData.email,
        bio: appData.bio,
        expertise: appData.expertise || [],
        profileImage: appData.profileImage || '',
        socialLinks: appData.socialLinks || {},
        
        // Stats
        totalCourses: 0,
        totalStudents: 0,
        totalRevenue: 0,
        averageRating: 0,
        totalReviews: 0,
        
        // Settings
        isActive: true,
        isVerified: false,
        revenueShare: 70, // 70% to instructor, 30% platform
        
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      const docRef = await addDoc(instructorsRef, instructor);
      
      // 📧 Send professional approval email
      try {
        await emailService.sendInstructorApprovalEmail({
          name: appData.name,
          email: appData.email
        });
        console.log('✅ Approval email sent successfully');
      } catch (emailError) {
        console.error('⚠️ Failed to send approval email:', emailError);
        // Don't fail the approval if email fails
      }
      
      return { id: docRef.id, ...instructor };
    } catch (error) {
      console.error('Error approving application:', error);
      throw error;
    }
  }

  /**
   * Reject instructor application
   */
  async rejectApplication(applicationId, adminId, reason = '') {
    try {
      const appRef = doc(db, COLLECTIONS.APPLICATIONS, applicationId);
      const appDoc = await getDoc(appRef);
      
      if (!appDoc.exists()) {
        throw new Error('Application not found');
      }
      
      const appData = appDoc.data();
      
      // Update application status
      await updateDoc(appRef, {
        status: 'rejected',
        reviewedAt: Timestamp.now(),
        reviewedBy: adminId,
        reviewNotes: reason
      });
      
      // 📧 Send professional rejection email
      try {
        await emailService.sendInstructorRejectionEmail(
          {
            name: appData.name,
            email: appData.email
          },
          reason
        );
        console.log('✅ Rejection email sent successfully');
      } catch (emailError) {
        console.error('⚠️ Failed to send rejection email:', emailError);
        // Don't fail the rejection if email fails
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error rejecting application:', error);
      throw error;
    }
  }

  /**
   * Get instructor profile
   */
  async getInstructorProfile(instructorId) {
    try {
      const instructorRef = doc(db, COLLECTIONS.INSTRUCTORS, instructorId);
      const instructorDoc = await getDoc(instructorRef);
      
      if (!instructorDoc.exists()) return null;
      
      return { id: instructorDoc.id, ...instructorDoc.data() };
    } catch (error) {
      console.error('Error getting instructor:', error);
      return null;
    }
  }

  /**
   * Get instructor by userId
   */
  async getInstructorByUserId(userId) {
    try {
      const instructorsRef = collection(db, COLLECTIONS.INSTRUCTORS);
      const q = query(instructorsRef, where('userId', '==', userId), limit(1));
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    } catch (error) {
      console.error('Error getting instructor by userId:', error);
      return null;
    }
  }

  /**
   * Update instructor profile
   */
  async updateInstructorProfile(instructorId, updates) {
    try {
      const instructorRef = doc(db, COLLECTIONS.INSTRUCTORS, instructorId);
      await updateDoc(instructorRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating instructor:', error);
      throw error;
    }
  }

  /**
   * Publish a course
   */
  async publishCourse(instructorId, courseData) {
    try {
      const coursesRef = collection(db, COLLECTIONS.COURSES);
      
      const course = {
        ...courseData,
        instructorId,
        status: 'pending_review', // pending_review, approved, rejected, draft
        isPublic: false,
        price: courseData.price || 0,
        enrollmentCount: 0,
        revenue: 0,
        averageRating: 0,
        reviewCount: 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        publishedAt: null
      };
      
      const docRef = await addDoc(coursesRef, course);
      
      // Update instructor course count
      const instructorRef = doc(db, COLLECTIONS.INSTRUCTORS, instructorId);
      await updateDoc(instructorRef, {
        totalCourses: increment(1)
      });
      
      return { id: docRef.id, ...course };
    } catch (error) {
      console.error('Error publishing course:', error);
      throw error;
    }
  }

  /**
   * Publish a session
   */
  async publishSession(instructorId, sessionData) {
    try {
      const sessionsRef = collection(db, COLLECTIONS.SESSIONS);
      
      const session = {
        ...sessionData,
        instructorId,
        status: 'pending_review',
        isPublic: false,
        price: sessionData.price || 0,
        enrollmentCount: 0,
        maxStudents: sessionData.maxStudents || 30,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      const docRef = await addDoc(sessionsRef, session);
      return { id: docRef.id, ...session };
    } catch (error) {
      console.error('Error publishing session:', error);
      throw error;
    }
  }

  /**
   * Get instructor's courses
   */
  async getInstructorCourses(instructorId) {
    try {
      const coursesRef = collection(db, COLLECTIONS.COURSES);
      const q = query(
        coursesRef,
        where('instructorId', '==', instructorId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting instructor courses:', error);
      return [];
    }
  }

  /**
   * Get instructor's sessions
   */
  async getInstructorSessions(instructorId) {
    try {
      const sessionsRef = collection(db, COLLECTIONS.SESSIONS);
      const q = query(
        sessionsRef,
        where('instructorId', '==', instructorId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting instructor sessions:', error);
      return [];
    }
  }

  /**
   * Get instructor analytics
   */
  async getInstructorAnalytics(instructorId) {
    try {
      const instructor = await this.getInstructorProfile(instructorId);
      const courses = await this.getInstructorCourses(instructorId);
      const sessions = await this.getInstructorSessions(instructorId);
      
      // Calculate analytics
      const totalEnrollments = courses.reduce((sum, c) => sum + (c.enrollmentCount || 0), 0) +
                              sessions.reduce((sum, s) => sum + (s.enrollmentCount || 0), 0);
      
      const totalRevenue = courses.reduce((sum, c) => sum + (c.revenue || 0), 0) +
                          sessions.reduce((sum, s) => sum + (s.revenue || 0), 0);
      
      const avgRating = instructor?.averageRating || 0;
      const totalReviews = instructor?.totalReviews || 0;
      
      return {
        instructor,
        stats: {
          totalCourses: courses.length,
          totalSessions: sessions.length,
          totalEnrollments,
          totalRevenue,
          averageRating: avgRating,
          totalReviews,
          activeCourses: courses.filter(c => c.status === 'approved').length,
          pendingCourses: courses.filter(c => c.status === 'pending_review').length
        },
        recentCourses: courses.slice(0, 5),
        recentSessions: sessions.slice(0, 5)
      };
    } catch (error) {
      console.error('Error getting analytics:', error);
      throw error;
    }
  }

  /**
   * Record course enrollment (for revenue tracking)
   */
  async recordEnrollment(courseId, studentId, price) {
    try {
      const enrollmentsRef = collection(db, COLLECTIONS.ENROLLMENTS);
      
      const enrollment = {
        courseId,
        studentId,
        price,
        enrolledAt: Timestamp.now(),
        status: 'active'
      };
      
      await addDoc(enrollmentsRef, enrollment);
      
      // Update course stats
      const courseRef = doc(db, COLLECTIONS.COURSES, courseId);
      const courseDoc = await getDoc(courseRef);
      
      if (courseDoc.exists()) {
        const courseData = courseDoc.data();
        const instructorShare = (price * (courseData.revenueShare || 70)) / 100;
        
        await updateDoc(courseRef, {
          enrollmentCount: increment(1),
          revenue: increment(price)
        });
        
        // Update instructor stats
        if (courseData.instructorId) {
          const instructorRef = doc(db, COLLECTIONS.INSTRUCTORS, courseData.instructorId);
          await updateDoc(instructorRef, {
            totalStudents: increment(1),
            totalRevenue: increment(instructorShare)
          });
          
          // Record earning
          await addDoc(collection(db, COLLECTIONS.EARNINGS), {
            instructorId: courseData.instructorId,
            courseId,
            amount: instructorShare,
            platformFee: price - instructorShare,
            transactionDate: Timestamp.now(),
            status: 'pending_payout'
          });
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error recording enrollment:', error);
      throw error;
    }
  }

  /**
   * Get all instructors (for marketplace)
   */
  async getAllInstructors(limitCount = 50) {
    try {
      const instructorsRef = collection(db, COLLECTIONS.INSTRUCTORS);
      const q = query(
        instructorsRef,
        where('isActive', '==', true),
        orderBy('totalStudents', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting all instructors:', error);
      return [];
    }
  }

  /**
   * Get pending applications (for admin)
   */
  async getPendingApplications() {
    try {
      const applicationsRef = collection(db, COLLECTIONS.APPLICATIONS);
      const q = query(
        applicationsRef,
        where('status', '==', 'pending'),
        orderBy('submittedAt', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting pending applications:', error);
      return [];
    }
  }
}

const instructorService = new InstructorService();
export default instructorService;
